import { create } from 'zustand'

/**
 * Transient notifications, kept outside React so any layer can raise one.
 *
 * A mutation handler in a dialog, an error branch in the fetch wrapper and a route loader
 * all need to say "that worked" or "that did not", and none of them should have to be
 * handed a callback to do it. `toast.success(...)` works from anywhere; the container
 * renders whatever is in the store.
 */

export type ToastTone = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  tone: ToastTone
  title?: string
  message: string
  /** Milliseconds until it disappears on its own. 0 means it stays until dismissed. */
  duration: number
}

interface ToastState {
  toasts: Toast[]
  push: (toast: Omit<Toast, 'id'>) => number
  dismiss: (id: number) => void
  clear: () => void
}

/**
 * Success and info are read at a glance and then in the way. An error is the one a user
 * may need to copy a reference out of, or read twice, so it waits to be dismissed.
 */
const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 5000,
  error: 0,
}

let nextId = 1

// Timers live here rather than in the store so that dismissing a toast early -- by hand or
// by `clear()` on sign-out -- cannot leave a timer running that pops the next toast to
// take the same id.
const timers = new Map<number, ReturnType<typeof setTimeout>>()

export const useToasts = create<ToastState>((set, get) => ({
  toasts: [],

  push: (toast) => {
    const id = nextId++
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }))
    if (toast.duration > 0) {
      timers.set(
        id,
        setTimeout(() => get().dismiss(id), toast.duration),
      )
    }
    return id
  },

  dismiss: (id) => {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },

  clear: () => {
    timers.forEach(clearTimeout)
    timers.clear()
    set({ toasts: [] })
  },
}))

interface ToastOptions {
  title?: string
  duration?: number
}

function raise(tone: ToastTone, message: string, options: ToastOptions = {}): number {
  return useToasts.getState().push({
    tone,
    message,
    title: options.title,
    duration: options.duration ?? DEFAULT_DURATION[tone],
  })
}

export const toast = {
  success: (message: string, options?: ToastOptions) => raise('success', message, options),
  error: (message: string, options?: ToastOptions) => raise('error', message, options),
  info: (message: string, options?: ToastOptions) => raise('info', message, options),
  dismiss: (id: number) => useToasts.getState().dismiss(id),
  clear: () => useToasts.getState().clear(),
}
